import Service from '@/Network/Services/Service'
import request from '@/Network/Requests/request'
import {
  PaymentPolicyResponse,
  PaymentPolicyByIdResponse,
  UpdatePaymentPolicyParams,
  LinkPoliciesToScheduleResponse,
  ListSchedulePolicyLinksResponse
} from '@/Network/Types/Responses/DsPaymentPolicyResponses'
import {
  ListPaymentPolicyParams,
  CreatePaymentPolicyParams,
  LinkPoliciesToScheduleRequestData
} from '@/Network/Types/Requests/DsPaymentPolicyRequests'

export default class PaymentPolicyService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/ds-payment-policy'
  }

  async list(params: ListPaymentPolicyParams): Promise<PaymentPolicyResponse> {
    try {
      const response = await request.get<PaymentPolicyResponse>(this.getCompletePath(), params)
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as políticas de pagamento.')
    }
  }

  async listByScheduleId(
    scheduleId: number,
    params?: ListPaymentPolicyParams
  ): Promise<PaymentPolicyResponse> {
    try {
      const response = await request.get<PaymentPolicyResponse>(
        `${this.getCompletePath()}/schedule/${scheduleId}`,
        params
      )
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as políticas de pagamento da escala.')
    }
  }

  async create(
    scheduleId: number,
    data: Omit<CreatePaymentPolicyParams, 'scheduleId'>
  ): Promise<PaymentPolicyResponse> {
    try {
      const url = `${this.getCompletePath()}`
      const response = await request.post<PaymentPolicyResponse>(url, {
        scheduleId,
        ...data
      })
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível criar a política de pagamento.')
    }
  }

  async disable(id: number, scheduleId: number): Promise<PaymentPolicyResponse> {
    try {
      const { data } = await request.put<PaymentPolicyResponse>(
        `${this.getCompletePath()}/${id}/disable/${scheduleId}`,
        null
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível desativar a política de pagamento.')
    }
  }

  async activate(id: number, scheduleId: number): Promise<PaymentPolicyResponse> {
    try {
      const { data } = await request.put<PaymentPolicyResponse>(
        `${this.getCompletePath()}/${id}/activate/${scheduleId}`,
        null
      )

      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível ativar a política de pagamento.')
    }
  }

  async getById(id: number): Promise<PaymentPolicyByIdResponse> {
    try {
      const { data } = await request.get<PaymentPolicyByIdResponse>(
        `${this.getCompletePath()}/${id}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os detalhes da política de pagamento.')
    }
  }

  async update(id: number, scheduleId: number, updatePaymentPolicy: UpdatePaymentPolicyParams) {
    try {
      const url = `${this.getCompletePath()}/${id}/${scheduleId}`
      const { data } = await request.patch(url, updatePaymentPolicy)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar a política de pagamento.')
    }
  }

  async linkPoliciesToSchedule(
    scheduleId: number,
    linksPayload: LinkPoliciesToScheduleRequestData
  ): Promise<LinkPoliciesToScheduleResponse> {
    try {
      const url = `${this.getCompletePath()}/schedule/${scheduleId}/links`
      const response = await request.post<LinkPoliciesToScheduleResponse>(url, linksPayload)
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível salvar os vínculos de política de pagamento da escala.'
      )
    }
  }

  async listSchedulePolicyLinks(scheduleId: number): Promise<ListSchedulePolicyLinksResponse> {
    try {
      const url = `${this.getCompletePath()}/schedule/${scheduleId}/policy-links`
      const response = await request.get<ListSchedulePolicyLinksResponse>(url)
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os vínculos de política de pagamento da escala.'
      )
    }
  }
}
